import React, { useState, useEffect, useRef } from "react";
import Step1Form from "./Step1Form.js";
import Step2Form from "./Step2Form.js";
import { generateQuotePDF } from "./PdfGenerator.js";
import { getMinEventDateValue, isDateBeforeMinEventDate } from "./dateUtils.js";
import { detectLanguage, getTranslations, locales } from "./i18n.js";

export default function QuoteConfigurator() {
  const language = detectLanguage();
  const copy = getTranslations(language);
  const t = copy.b2b;
  const common = copy.common;
  const locale = locales[language] || locales.it;

  const [form, setForm] = useState({
    start_date: "",
    location: "",
    latitude: "",
    longitude: "",
    amount: "",
    amount_rain: "5mm",
    start_hour: "12:00",
    end_hour: "18:00",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    notes: "",
    fiscal_code: "",
    gender: "",
    birth_date: "",
    birth_country: "",
    residence: "",
    cap: "",
    confirm_privacy: false,
    confirm_rain_protection: false,
    supplier_code: "",   // 👈 aggiunto
    wedding_of: "",      // 👈 aggiunto
  });

  const [loading, setLoading] = useState(false);
  const [price, setPrice] = useState(null);
  const [step, setStep] = useState(1);
  const [pdfLibReady, setPdfLibReady] = useState(false);
  const locationRef = useRef(null);

  const LOGO_URL = "https://wedly.it/wp-content/uploads/2025/01/logo-wedly_1@4x-1024x348.png";
  const PRIMARY = "#0f1943";

  // === jsPDF Loader ===
  useEffect(() => {
    const hasJsPDF = !!window.jspdf;
    const hasAutoTable = !!window.jspdf?.autoTable;
    const load = async () => {
      if (!hasJsPDF) {
        await new Promise((res, rej) => {
          const s = document.createElement("script");
          s.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
          s.onload = res;
          s.onerror = rej;
          document.body.appendChild(s);
        });
      }
      if (!hasAutoTable) {
        await new Promise((res, rej) => {
          const s = document.createElement("script");
          s.src =
            "https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.3/jspdf.plugin.autotable.min.js";
          s.onload = res;
          s.onerror = rej;
          document.body.appendChild(s);
        });
      }
      setPdfLibReady(true);
    };
    load().catch(() => setPdfLibReady(false));
  }, []);

  // === Google Places Autocomplete ===
  useEffect(() => {
    const waitForGoogle = setInterval(() => {
      if (window.google?.maps?.places && locationRef.current) {
        clearInterval(waitForGoogle);

        const autocomplete = new window.google.maps.places.Autocomplete(locationRef.current, {
          componentRestrictions: { country: "it" },
          fields: ["formatted_address", "geometry"],
        });

        autocomplete.addListener("place_changed", () => {
          const place = autocomplete.getPlace();
          if (!place.geometry) return;
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          setForm((prev) => ({
            ...prev,
            location: place.formatted_address,
            latitude: lat,
            longitude: lng,
          }));
        });
      }
    }, 500);

    return () => clearInterval(waitForGoogle);
  }, []);

  // === Handlers ===
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      setForm((f) => ({ ...f, [name]: checked }));
      return;
    }

    if (name === "start_hour") {
      const startHour = parseInt(value.split(":")[0], 10);
      const endHour = Math.min(startHour + 9, 23);
      setForm((f) => ({
        ...f,
        start_hour: value,
        end_hour: String(endHour).padStart(2, "0") + ":00",
      }));
      return;
    }

    if (name === "location") {
      setForm((f) => ({ ...f, location: value, latitude: "", longitude: "" }));
      return;
    }

    setForm((f) => ({ ...f, [name]: value }));
  };

  // === Step 1: calcolo preventivo ===
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.start_date || !form.location || !form.amount) {
      alert(common.requiredB2BStep1);
      return;
    }

    if (isDateBeforeMinEventDate(form.start_date)) {
      alert(`${common.minDate} ${new Date(getMinEventDateValue()).toLocaleDateString(locale)}.`);
      return;
    }

    setLoading(true);
    setPrice(null);

    try {
      const res = await fetch("https://wedly.it/api/sendLead", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          caller: "getQuote",
          ...form,
        }),
      });
      const data = await res.json();
      if (data?.response?.price) setPrice(data.response.price);
      else
        alert(common.b2bQuoteUnavailable);
    } catch {
      alert(common.connectionError);
    } finally {
      setLoading(false);
    }
  };

  // === Step 2: invio via email ===
  const handleFinalize = async (e) => {
    e.preventDefault();

    if (
      !form.first_name ||
      !form.last_name ||
      !form.email ||
      !form.phone ||
      !form.fiscal_code ||
      !form.gender ||
      !form.birth_date ||
      !form.birth_country ||
      !form.residence ||
      !form.cap ||
      !form.confirm_privacy ||
      !form.confirm_rain_protection ||
      !form.supplier_code ||
      !form.wedding_of
    ) {
      alert(common.requiredB2BFinalize);
      return;
    }

    setLoading(true);

    try {
      await fetch("https://wedly.it/wp-json/api/send_b2b_quote_email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          supplier_code: form.supplier_code, // 👈 incluso
          wedding_of: form.wedding_of,       // 👈 incluso
          first_name: form.first_name,
          last_name: form.last_name,
          email: form.email,
          phone: form.phone,
          fiscal_code: form.fiscal_code,
          gender: form.gender,
          birth_date: form.birth_date,
          birth_country: form.birth_country,
          residence: form.residence,
          cap: form.cap,
          confirm_rain_protection: form.confirm_rain_protection ? common.yes : common.no,
          lang: language,
          notes: form.notes,
          location: form.location,
          start_date: form.start_date,
          start_hour: form.start_hour,
          end_hour: form.end_hour,
          amount: form.amount,
          amount_rain: form.amount_rain,
          price: price,
        }),
      });

      alert(common.sentB2B);
      window.location.href = language === "en" ? "https://wedly.it/en/preventiva-la-tua-polizza/" : "https://wedly.it/preventiva-la-tua-polizza/";
    } catch {
      alert(common.sendError);
    } finally {
      setLoading(false);
    }
  };

  // === Scarica PDF manualmente ===
  const handleDownloadPDF = async () => {
    await generateQuotePDF(form, price, PRIMARY, LOGO_URL, false, { language, locale, t, common });
  };

  const proceedToStep2 = () => {
    if (!price) {
      alert(common.calculateFirstB2B);
      return;
    }
    setStep(2);
  };

  const goBack = () => setStep(1);

  const formatCurrency = (v) =>
    Number(v || 0).toLocaleString(locale, { style: "currency", currency: "EUR" });

  const indemnity = Number(form.amount || 0) * 0.7;

  return (
    <div className="configurator-wrapper">
      <div className="configurator-card">
        <h2 className="title">
          ⚡ {t.heroBefore} <span>{t.heroHighlight}</span> {t.heroAfter}
        </h2>

        {step === 1 && (
          <Step1Form
            form={form}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            loading={loading}
            price={price}
            proceedToStep2={proceedToStep2}
            formatCurrency={formatCurrency}
            locationRef={locationRef}
            t={t}
          />
        )}

        {step === 2 && (
          <Step2Form
            form={form}
            handleChange={handleChange}
            handleFinalize={handleFinalize}
            handleDownloadPDF={handleDownloadPDF}
            loading={loading}
            pdfLibReady={pdfLibReady}
            price={price}
            indemnity={indemnity}
            formatCurrency={formatCurrency}
            goBack={goBack}
            t={t}
            common={common}
          />
        )}
      </div>
    </div>
  );
}
